import React, { useState } from "react";
import { Plus, Trash2, Save, X, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import { useCrud } from "./_useCrud";
import { PageHeader, Spinner, Card, PrimaryButton, SecondaryButton, Input, Textarea, Select, EmptyState, Badge } from "./_ui";

const CATS = ["Règlement", "Statuts", "PV de réunion", "Communiqué", "Charte", "Autre"];
const EMPTY = { title: "", description: "", url: "", category: "Règlement" };

export default function DocumentsPanel() {
  const { items, create, remove } = useCrud({
    listEndpoint: "/admin/documents",
    createEndpoint: "/admin/documents",
    deleteEndpoint: "/admin/documents",
  });
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const save = async () => {
    if (!form.title || !form.url) return toast.error("Titre et URL requis");
    const ok = await create(form, { successMsg: "Document ajouté" });
    if (ok) { setAdding(false); setForm(EMPTY); }
  };

  if (!items) return (<><PageHeader title="Documents" /><Spinner /></>);

  return (
    <div data-testid="admin-documents">
      <PageHeader
        title="Documents"
        subtitle="Bibliothèque interne : règlements, statuts, communiqués officiels."
        action={
          !adding && (
            <PrimaryButton onClick={() => setAdding(true)} data-testid="doc-create-btn">
              <Plus size={16} /> Ajouter un document
            </PrimaryButton>
          )
        }
      />

      {adding && (
        <Card className="p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="doc-title" />
            <Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {CATS.map((c) => <option key={c}>{c}</option>)}
            </Select>
          </div>
          <Input placeholder="URL du fichier (PDF, doc, image…)" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="mt-3" data-testid="doc-url" />
          <Textarea placeholder="Description (facultatif)" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-3" />
          <div className="flex gap-3 mt-4">
            <PrimaryButton onClick={save} data-testid="doc-save"><Save size={16} /> Enregistrer</PrimaryButton>
            <SecondaryButton onClick={() => { setAdding(false); setForm(EMPTY); }}><X size={16} /> Annuler</SecondaryButton>
          </div>
        </Card>
      )}

      {items.length === 0 ? (
        <EmptyState icon={FileText} title="Bibliothèque vide" hint="Ajoutez les documents officiels du mouvement." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((d) => (
            <Card key={d.id} className="p-5" data-testid={`doc-${d.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg bg-[#0052CC]/15 grid place-items-center text-[#7AB0FF] shrink-0">
                  <FileText size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-medium text-white truncate">{d.title}</h3>
                    <button onClick={() => remove(d.id, { confirm: "Supprimer ce document ?" })} className="text-slate-500 hover:text-[#CE1126] shrink-0"><Trash2 size={14} /></button>
                  </div>
                  <Badge color="slate">{d.category}</Badge>
                  {d.description && <p className="text-xs text-slate-400 mt-2 line-clamp-2">{d.description}</p>}
                  <a href={d.url} target="_blank" rel="noreferrer"
                    className="text-xs text-[#D4AF37] hover:underline mt-3 inline-flex items-center gap-1">
                    <Download size={11} /> Consulter
                  </a>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
